<template>
  <div class="auth-password-meter">
    <div class="auth-password-meter__bars">
      <div
        v-for="n in 3"
        :key="n"
        class="auth-password-meter__bar"
        :class="getBarClass(n)"
      ></div>
    </div>

    <div class="auth-password-meter__tooltip" v-if="password">
      <div class="auth-password-meter__rules">
        <div class="auth-password-meter__rule" :class="{ 'auth-password-meter__rule--pass': rules.hasMinLength }">
          <AuthIcon :name="rules.hasMinLength ? 'check-circle' : 'circle'" class="auth-password-meter__icon" />
          <span>At least 15 characters</span>
        </div>
        <div class="auth-password-meter__rule" :class="{ 'auth-password-meter__rule--pass': rules.hasMaxLength }">
          <AuthIcon :name="rules.hasMaxLength ? 'check-circle' : 'circle'" class="auth-password-meter__icon" />
          <span>No more than 64 characters</span>
        </div>
        <div class="auth-password-meter__rule" :class="{ 'auth-password-meter__rule--pass': rules.hasPrintableAsciiOnly }">
          <AuthIcon :name="rules.hasPrintableAsciiOnly ? 'check-circle' : 'circle'" class="auth-password-meter__icon" />
          <span>Only printable ASCII characters</span>
        </div>
        <div class="auth-password-meter__rule" :class="{ 'auth-password-meter__rule--pass': rules.hasNoSequentialCharacters }">
          <AuthIcon :name="rules.hasNoSequentialCharacters ? 'check-circle' : 'circle'" class="auth-password-meter__icon" />
          <span>No sequential characters (e.g. 1234, abcd)</span>
        </div>
        <div class="auth-password-meter__rule" :class="{ 'auth-password-meter__rule--pass': rules.hasNoRepeatedCharacters }">
          <AuthIcon :name="rules.hasNoRepeatedCharacters ? 'check-circle' : 'circle'" class="auth-password-meter__icon" />
          <span>No repeated characters (e.g. aa, 111)</span>
        </div>
        <div class="auth-password-meter__rule" :class="{ 'auth-password-meter__rule--pass': rules.hasNoPlatformName }">
          <AuthIcon :name="rules.hasNoPlatformName ? 'check-circle' : 'circle'" class="auth-password-meter__icon" />
          <span>Doesn't contain 'Remoni'</span>
        </div>
        <div class="auth-password-meter__rule" :class="{ 'auth-password-meter__rule--pass': rules.hasNoPersonalData }">
          <AuthIcon :name="rules.hasNoPersonalData ? 'check-circle' : 'circle'" class="auth-password-meter__icon" />
          <span>Doesn't contain your email, first or last name</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import AuthIcon from './AuthIcon.vue'
import { evaluatePassword } from '../utils/passwordPolicy.js'

const props = defineProps({
  password: { type: String, default: '' },
  email: { type: String, default: '' },
  firstName: { type: String, default: '' },
  lastName: { type: String, default: '' },
  additionalPersonalValues: { type: Array, default: () => [] },
})

const rules = computed(() => evaluatePassword(props.password, {
  email: props.email,
  firstName: props.firstName,
  lastName: props.lastName,
  additionalPersonalValues: props.additionalPersonalValues,
}))

const strength = computed(() => {
  if (!props.password) return 0

  const passedRules = Object.values(rules.value).filter(Boolean).length
  const totalRules = Object.keys(rules.value).length

  if (passedRules === totalRules) return 3
  if (passedRules >= Math.ceil(totalRules / 2)) return 2
  return 1
})

const getBarClass = (n) => {
  const s = Math.min(strength.value, 3)
  if (s >= n) {
    if (s === 1) return 'auth-password-meter__bar--weak'
    if (s === 2) return 'auth-password-meter__bar--medium'
    if (s === 3) return 'auth-password-meter__bar--strong'
  }
  return ''
}
</script>

<style scoped>
.auth-password-meter {
  margin-top: 8px;
}
.auth-password-meter__bars {
  display: flex;
  gap: 4px;
  height: 4px;
  margin-bottom: 8px;
}
.auth-password-meter__bar {
  flex: 1;
  background-color: var(--auth-border);
  border-radius: 2px;
  transition: background-color 0.3s;
}
.auth-password-meter__bar--weak {
  background-color: #ef4444;
}
.auth-password-meter__bar--medium {
  background-color: #eab308;
}
.auth-password-meter__bar--strong {
  background-color: #22c55e;
}
.auth-password-meter__tooltip {
  background-color: var(--auth-surface-alt);
  border: 1px solid var(--auth-border);
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  color: var(--auth-text);
  margin-top: 8px;
}
.auth-password-meter__rules {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.auth-password-meter__rule {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--auth-text);
}
.auth-password-meter__rule--pass {
  color: #22c55e;
}
.auth-password-meter__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
</style>
